import { fiscalMonths } from '../../../constants/utils';
import { formatPercentValue } from '../../../utils/formatUtils';
import { CURRENT_MONTH, CURRENT_WEEK } from '../constants';

// send headers array and position to insert the last element
const columnPositionAlteringFun = (headers, position, text = '') => {
  // Check if headers array is empty
  if (!headers || headers.length === 0) {
    return headers; // Return the original empty array
  }

  // Ensure the position is within the valid range
  if (position < 0 || position > headers.length) {
    position = headers.length; // Default to appending at the end
  }
  const modifiedWeekPosition = headers;

  // Remove the last element
  const lastElement = modifiedWeekPosition.pop();

  // Prepend the text to the Header property of the last element
  const modifiedElement = {
    ...lastElement,
    Header: text + '\n' + lastElement.Header,
  };

  // Insert the modified element at the specified position
  modifiedWeekPosition.splice(position, 0, modifiedElement);

  return modifiedWeekPosition;
};

// pass the options and store_id to get the branch label
function getBranchLabel(options, store_id) {
  const item = options.find((option) => option.branch_number === store_id);
  return item ? item?.label : null;
}

export const actualVsBudgetWeeklyHeaders = (data, branch = false) => {
  try {
    let headers = [];

    data?.forEach((item) => {
      const monthAbbreviation = fiscalMonths?.[item?.fiscal_month];
      const headerValue = `${monthAbbreviation} W${item?.fiscal_week}`;
      if (
        !headers?.some((header) => header?.Header === headerValue) &&
        item?.fiscal_week <= item?.ytd_week
      ) {
        headers?.push({
          Header: headerValue,
          accessor: headerValue?.replace(/\s/g, '_'),
        });
      }
    });

    // Sort headers by fiscal month and week
    headers?.sort((a, b) => {
      const extractDetails = (header) => {
        const parts = header?.Header?.split(' ');
        const month = Object.keys(fiscalMonths)?.find((key) => fiscalMonths?.[key] === parts?.[0]);
        const weekPart = parts?.[1];
        const week = weekPart ? parseInt(weekPart.substring(1), 10) : 0;
        return { month: parseInt(month, 10), week };
      };

      const detailsA = extractDetails(a);
      const detailsB = extractDetails(b);

      if (detailsA?.month !== detailsB?.month) {
        return detailsA?.month - detailsB?.month;
      }
      return detailsA?.week - detailsB?.week;
    });

    // this is done as an enchancements ask so the client can see the latest week/month first
    columnPositionAlteringFun(headers, 0, CURRENT_WEEK);
    if (branch) {
      headers?.unshift(
        {
          Header: () => <span style={{ width: '136px', display: 'inline-block' }}>Branch</span>,
          accessor: 'branch',
        },
        { Header: 'Total', accessor: 'ytd_spend_to_plan' }
      );
    } else {
      headers?.unshift(
        {
          Header: () => <span style={{ width: '136px', display: 'inline-block' }}>Channel</span>,
          accessor: 'channel',
        },
        { Header: 'YTD', accessor: 'ytd_spend_to_plan' }
      );
    }
    return headers?.filter((header) => header !== undefined && header !== null);
  } catch (e) {
    console.error('error actualVsBudgetWeeklyHeaders--->', e);
  }
};

export const actualVsBudgetWeeklyRowData = ({
  data,
  total = false,
  branchData = false,
  options = [],
}) => {
  try {
    let aggregateData = [];
    // branch is the accessors for the branch column
    if (!branchData) {
      data?.forEach((item) => {
        const monthAbbreviation = fiscalMonths?.[item?.fiscal_month];
        const newKey = `${monthAbbreviation}_W${item?.fiscal_week}`;
        let channelObj = aggregateData?.find((obj) => obj?.channel === item?.channel);

        if (!channelObj) {
          channelObj = {
            channel: item?.channel,
            ytd_spend_to_plan: formatPercentValue(item?.ytd_spend_to_plan),
          };
          aggregateData?.push(channelObj);
        } else {
          channelObj.ytd_spend_to_plan = formatPercentValue(item?.ytd_spend_to_plan); // Update ytd_spend_to_plan for the channel
        }
        channelObj[newKey] = formatPercentValue(item?.spend_to_plan);
      });

      // added this for the total case to show the total row
      if (total && aggregateData?.length > 0) {
        aggregateData[0].channel = 'Total';
        const boldedData = aggregateData.map((item) => {
          const boldedItem = {};
          for (const key in item) {
            if (item.hasOwnProperty(key)) {
              boldedItem[key] = <span style={{ fontWeight: 700 }}>{item?.[key]}</span>;
            }
          }
          return boldedItem;
        });
        return boldedData;
      }
      // Sort the channels alphabetically
      const aggregateDataCopy = [...aggregateData];
      aggregateDataCopy.sort((a, b) => a?.channel?.localeCompare(b?.channel));
      const aggregateDataAfterSorting = aggregateDataCopy;

      return aggregateDataAfterSorting;
    } else {
      data.forEach((item) => {
        const monthAbbreviation = fiscalMonths?.[item?.fiscal_month];
        const newKey = `${monthAbbreviation}_W${item?.fiscal_week}`;
        let branchObj = aggregateData?.find((obj) => obj?.store_id === item?.store_id);

        if (!branchObj) {
          branchObj = {
            branch: getBranchLabel(options, item?.store_id),
            store_id: item?.store_id,
            ytd_spend_to_plan: formatPercentValue(item?.ytd_spend_to_plan),
          };
          aggregateData?.push(branchObj);
        } else {
          branchObj.ytd_spend_to_plan = formatPercentValue(item?.ytd_spend_to_plan); // Update ytd_spend_to_plan for the channel
        }
        branchObj[newKey] = formatPercentValue(item?.spend_to_plan);
      });

      // Sort the branches alphabetically
      const aggregateDataCopy = [...aggregateData];
      aggregateDataCopy.sort((a, b) => a?.branch?.localeCompare(b?.branch));
      const aggregateDataAfterSorting = aggregateDataCopy;
      return aggregateDataAfterSorting;
    }
  } catch (e) {
    console.error('error--->', e);
  }
};

export const actualVsBudgetMonthlyHeaders = (ytd_month, branch = false) => {
  try {
    let headers = [];

    Object.keys(fiscalMonths)?.forEach((month) => {
      if (parseInt(month, 10) <= ytd_month) {
        const monthAbbreviation = fiscalMonths?.[month];
        headers?.push({
          Header: monthAbbreviation,
          accessor: `_${monthAbbreviation}`,
        });
      }
    });

    // Sort headers by fiscal month
    headers?.sort((a, b) => {
      const monthA = Object.keys(fiscalMonths)?.find((key) => fiscalMonths?.[key] === a?.Header);
      const monthB = Object.keys(fiscalMonths)?.find((key) => fiscalMonths?.[key] === b?.Header);
      return parseInt(monthA, 10) - parseInt(monthB, 10);
    });
    // this is done as an enchancements ask so the client can see the latest week/month first
    columnPositionAlteringFun(headers, 0, CURRENT_MONTH);

    // Optionally, add any additional headers needed at the beginning of the array
    if (branch) {
      headers?.unshift(
        {
          Header: () => <span style={{ width: '136px', display: 'inline-block' }}>Branch</span>,
          accessor: 'branch',
        },
        { Header: 'Total', accessor: '_YTD' }
      );
    } else {
      headers?.unshift(
        {
          Header: () => <span style={{ width: '136px', display: 'inline-block' }}>Channel</span>,
          accessor: 'channel',
        },
        { Header: 'YTD', accessor: '_YTD' }
      );
    }
    return headers?.filter((header) => header !== undefined && header !== null);
  } catch (e) {
    console.error('error actualVsBudgetWeeklyHeaders--->', e);
  }
};

export const actualVsBudgetMonthlyRowData = ({
  data,
  total = false,
  branchData = false,
  options = [],
}) => {
  try {
    let channelData = {};
    if (!branchData) {
      data?.forEach((item) => {
        const monthAbbreviation = `_${fiscalMonths?.[item?.fiscal_month]}`;
        if (!channelData[item.channel]) {
          channelData[item.channel] = {
            channel: item?.channel,
            _YTD: formatPercentValue(item?.ytd_spend_to_plan),
          };
        }
        // Update the month spend if it's within the YTD month range
        if (item?.fiscal_month <= item?.ytd_month) {
          channelData[item.channel][monthAbbreviation] = formatPercentValue(item?.spend_to_plan);
        }
      });

      // Convert the object to an array of objects
      let result = Object.values(channelData);
      if (total && result?.length > 0) {
        result[0].channel = 'Total';
        const boldedData = result?.map((item) => {
          const boldedItem = {};
          for (const key in item) {
            if (item.hasOwnProperty(key)) {
              boldedItem[key] = <span style={{ fontWeight: 700 }}>{item?.[key]}</span>;
            }
          }
          return boldedItem;
        });
        return boldedData;
      }
      // Sort the channels alphabetically
      result?.sort((a, b) => a?.channel?.localeCompare(b?.channel));
      return result;
    } else {
      data?.forEach((item) => {
        const monthAbbreviation = `_${fiscalMonths?.[item?.fiscal_month]}`;
        if (!channelData[item.store_id]) {
          channelData[item.store_id] = {
            branch: getBranchLabel(options, item?.store_id),
            store_id: item?.store_id,
            _YTD: formatPercentValue(item?.ytd_spend_to_plan),
          };
        }
        // Update the month spend if it's within the YTD month range
        if (item?.fiscal_month <= item?.ytd_month) {
          channelData[item.store_id][monthAbbreviation] = formatPercentValue(item?.spend_to_plan);
        }
      });

      // Convert the object to an array of objects
      let result = Object.values(channelData);

      // Sort the branches alphabetically
      result?.sort((a, b) => a?.branch?.localeCompare(b?.branch));
      return result;
    }
  } catch (e) {
    console.error('error actualVsBudgetMonthlyRowData--->', e);
  }
};

// channel_roll_up_map logic channels logic -> to get the combined channel separated and send combined channel separately in filters payload
export const seprateCombinedChannel = (selectedChannels, rollUpChannels) => {
  // Create a map where each channel maps to its roll-up channels
  const uniqueChannelsMap = rollUpChannels.reduce((map, { roll_up_channel, channel }) => {
    if (!map[roll_up_channel]) {
      map[roll_up_channel] = new Set();
    }
    map[roll_up_channel].add(channel);
    return map;
  }, {});

  // Convert the map to an array of objects with roll_up_channel and channels
  const uniqueChannels = Object.entries(uniqueChannelsMap)?.map(([roll_up_channel, channels]) => ({
    roll_up_channel,
    channels: [...channels],
  }));

  // Create a copy of selectedChannels
  let updatedChannels = [...selectedChannels];

  // Add roll-up channels to the updatedChannels if the selectedChannels include the roll-up channel
  uniqueChannels.forEach((item) => {
    const index = updatedChannels.indexOf(item?.roll_up_channel);
    if (index !== -1) {
      // Remove the roll_up_channel from updatedChannels
      updatedChannels.splice(index, 1);
      // Add the channels to updatedChannels
      updatedChannels.push(...item.channels);
    }
  });

  return updatedChannels;
};

export const overrideStoreIdFilterForSelectedBranches = (filters, selectedBranches, options) => {
  if (!Array.isArray(filters)) return [];

  const storeIds =
    Array.isArray(selectedBranches) && selectedBranches?.length > 0
      ? selectedBranches
      : Array.isArray(options)
        ? [...new Set(options?.map((item) => item?.value).filter(Boolean))]
        : [];

  return filters.map((filter) => {
    if (filter?.col === 'store_id' && filter?.op === 'IN') {
      return {
        ...filter,
        val: storeIds,
      };
    }
    return filter;
  });
};
